
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { CartStore, CartItem, StockItem } from "@/types/stock";

export const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  isLoading: true,
  channel: null,

  initialize: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ isLoading: false, items: [] });
        return;
      }

      const { data: items, error } = await supabase
        .from("cart_items")
        .select(`
          *,
          stock_item:stock_items (
            *
          )
        `)
        .eq("created_by", user.id);

      if (error) throw error;

      const formattedItems = items.map(item => ({
        ...item,
        stock_item: {
          ...item.stock_item,
          data: typeof item.stock_item.data === 'string' 
            ? JSON.parse(item.stock_item.data)
            : item.stock_item.data
        }
      })) as CartItem[];

      set({ items: formattedItems, isLoading: false });
    } catch (error) {
      console.error("Error initializing cart:", error);
      set({ isLoading: false });
    }
  },

  addItem: async (stockItemId, quantity) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: existingItem } = await supabase
        .from("cart_items")
        .select("*")
        .eq("stock_item_id", stockItemId)
        .eq("created_by", user.id)
        .single();

      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        const { error } = await supabase
          .from("cart_items")
          .update({ quantity: newQuantity })
          .eq("id", existingItem.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("cart_items")
          .insert({
            stock_item_id: stockItemId,
            quantity: quantity,
            created_by: user.id
          });

        if (error) throw error;
      }

      await get().initialize();
    } catch (error) {
      console.error("Error adding item to cart:", error);
      throw error;
    }
  },

  removeItem: async (itemId) => {
    try {
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;

      set(state => ({
        items: state.items.filter(item => item.id !== itemId)
      }));
    } catch (error) {
      console.error("Error removing item from cart:", error);
      throw error;
    }
  },

  calculateTotal: () => {
    return get().items.reduce((total, item) => {
      return total + (item.quantity * item.stock_item.data.price);
    }, 0);
  },

  cleanup: () => {
    set({ items: [], isLoading: false });
  }
}));
